// Detects stack overflows thrown inside Chromium-on-iOS browser-injected scripts
// (LOTTOSOCIAL-PWA-1NCA, 1NAR, 1NAV, 1NC5, 1NC6, 1NC7).
//
// Chrome Mobile iOS / the Google iOS app inject their own minified scripts at document start
// (e.g. autofill / DOM traversal). WebKit attributes those frames to the page URL at injection
// time (e.g. /quick-launch, /mainTabs/tabs/home) rather than to a .js bundle. Their internal
// mutual recursion (Jk <-> Lk) overflows the stack on deep Ionic/shadow-DOM trees. This is not
// app code and cannot be fixed in the app.
//
// Kept pure (origins passed in) so it can be unit-tested with representative Sentry events.

interface FrameLike {
    filename?: string;
    function?: string;
}

interface ExceptionLike {
    type?: string;
    value?: string;
    mechanism?: { type?: string };
    stacktrace?: { frames?: FrameLike[] };
}

export interface EventLike {
    exception?: { values?: ExceptionLike[] };
    contexts?: Record<string, any>;
    request?: { url?: string; headers?: Record<string, unknown> };
}

// Exact browser names observed in Sentry for this issue family (lower-cased). Kept exact on
// purpose: suppression drops events, so unobserved aliases are left reportable.
const CHROMIUM_IOS_BROWSERS = new Set(['chrome mobile ios', 'google']);
// Any two alternating minified names are accepted (not just "Jk"/"Lk"): the injected script is
// re-minified per Chrome release, so hard-coding names would silently stop matching after updates.
// False positives are bounded by the iOS + Chromium-browser, onerror, page-route-only frame,
// and strict-alternation requirements.
const MINIFIED_FN_RE = /^[A-Za-z_$][A-Za-z0-9_$]{0,2}$/;
const MIN_RECURSIVE_FRAMES = 6;
// Observed events carry 4 unnamed frames at the innermost end of the stack (the code that
// actually overflows). Allow a small leading/trailing block only — never interleaved frames.
const MAX_UNNAMED_FRAMES = 6;

const isUnnamed = (fn?: string): boolean => !fn || fn === '?' || fn === '<anonymous>';

// iPhone/iPad/iPod UA tokens (case-insensitive for defensively normalised UA strings).
const IOS_DEVICE_UA_RE = /\b(iphone|ipad|ipod)\b/i;
// iPadOS desktop-mode UAs report "Macintosh"; only treat them as iOS when the WebKit
// "Mobile/<build>" token is also present (desktop macOS browsers never send it).
const IPADOS_DESKTOP_UA_RE = /\bmacintosh\b/i;
const IOS_MOBILE_BUILD_RE = /\bmobile\/[0-9a-z]+/i;
// CriOS = Chrome for iOS, GSA = Google iOS app (Sentry labels it "Google").
const CHROME_IOS_UA_RE = /\bcrios\/\d/i;
const CHROMIUM_IOS_UA_RE = /\b(crios|gsa)\/\d/i;

function isIOSDeviceUserAgent(userAgent: string): boolean {
    return IOS_DEVICE_UA_RE.test(userAgent) ||
        (IPADOS_DESKTOP_UA_RE.test(userAgent) && IOS_MOBILE_BUILD_RE.test(userAgent));
}

/** True when the User-Agent string belongs to Chrome for iOS or the Google iOS app. */
export function isChromiumIOSUserAgent(userAgent?: string | null): boolean {
    if (!userAgent || typeof userAgent !== 'string') return false;
    return isIOSDeviceUserAgent(userAgent) && CHROMIUM_IOS_UA_RE.test(userAgent);
}

/** True when the User-Agent string belongs to Chrome for iOS only (not the Google app). */
export function isChromeIOSUserAgent(userAgent?: string | null): boolean {
    if (!userAgent || typeof userAgent !== 'string') return false;
    return isIOSDeviceUserAgent(userAgent) && CHROME_IOS_UA_RE.test(userAgent);
}

/** True when a Sentry contexts.os.name value denotes iOS or iPadOS (any casing / version suffix). */
export function isIOSOsName(osName: unknown): boolean {
    const normalized = String(osName ?? '').trim().toLowerCase();
    return /^(ios|ipados)\b/.test(normalized);
}

/**
 * Case-insensitive lookup of the User-Agent request header. Accepts string values or arrays of
 * strings (first string entry wins); returns '' when absent or of an unexpected type.
 */
export function getRequestUserAgent(headers?: Record<string, unknown> | null): string {
    if (!headers || typeof headers !== 'object') return '';
    for (const [key, value] of Object.entries(headers)) {
        if (key.toLowerCase() !== 'user-agent') continue;
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (Array.isArray(value)) {
            const first = value.find(v => typeof v === 'string' && v.trim());
            if (first) return (first as string).trim();
        }
    }
    return '';
}

/** Returns the trimmed UA string, or '' for non-strings / whitespace-only values. */
function normalizeUserAgent(userAgent: unknown): string {
    return typeof userAgent === 'string' ? userAgent.trim() : '';
}

/** Browser-side User-Agent fallback; returns '' outside a browser or if access throws. */
export function getNavigatorUserAgent(): string {
    try {
        return normalizeUserAgent((globalThis as any)?.navigator?.userAgent);
    } catch {
        return '';
    }
}

/**
 * Platform check for Chromium-on-iOS events.
 *
 * NOTE: event.contexts.os / event.contexts.browser are NOT populated by the browser SDK at
 * beforeSend time — Sentry derives them server-side from the User-Agent during ingestion.
 * Relying on contexts alone meant the filter never matched in production (LOTTOSOCIAL-PWA-1NCP).
 *
 * Sources are resolved in strict priority order and the FIRST available one is authoritative
 * (no OR across sources, so conflicting sources cannot produce a false positive):
 *   1. explicit `userAgent` argument (non-blank after trimming)
 *   2. event.request.headers User-Agent (set by the SDK's HttpContext integration; non-blank)
 *   3. event.contexts.os/browser — only when BOTH names are present (server-derived; partial
 *      contexts are ignored so they cannot cause a false negative)
 *   4. navigator.userAgent (last-resort browser fallback)
 */
export function isChromiumIOSEnvironment(event: EventLike, userAgent?: string | null): boolean {
    const explicitUA = normalizeUserAgent(userAgent);
    if (explicitUA) return isChromiumIOSUserAgent(explicitUA);

    const requestUA = getRequestUserAgent(event?.request?.headers);
    if (requestUA) return isChromiumIOSUserAgent(requestUA);

    const osName = String(event?.contexts?.os?.name ?? '').trim();
    const browserName = String(event?.contexts?.browser?.name ?? '').trim().toLowerCase();
    if (osName && browserName) {
        return isIOSOsName(osName) && CHROMIUM_IOS_BROWSERS.has(browserName);
    }

    return isChromiumIOSUserAgent(getNavigatorUserAgent());
}

/** Builds the set of origins whose page-route frames may be treated as injected-script frames. */
export function buildAllowedOrigins(documentOrigin: string, appHost: string): Set<string> {
    const origins = new Set<string>();
    if (documentOrigin) origins.add(documentOrigin);
    try {
        // Accept either a bare host ("app.example.com") or an absolute URL ("https://app.example.com").
        if (appHost) {
            const candidate = /^https?:\/\//i.test(appHost) ? appHost : `https://${appHost}`;
            origins.add(new URL(candidate).origin);
        }
    } catch {
        // Ignore an invalid host; documentOrigin alone still applies.
    }
    return origins;
}

function isInjectedPageRouteFrame(
    filename: string,
    allowedOrigins: Set<string>,
    documentOrigin: string,
): boolean {
    if (!filename) return false;
    const looksAbsolute = /^https?:\/\//i.test(filename) || filename.startsWith('//');
    if (!looksAbsolute && !filename.startsWith('/')) return false;
    let pathname: string;
    try {
        const protocol = new URL(documentOrigin).protocol;
        const normalized = filename.startsWith('//') ? `${protocol}${filename}` : filename;
        const parsed = new URL(normalized, documentOrigin);
        if (!allowedOrigins.has(parsed.origin)) return false;
        pathname = parsed.pathname;
    } catch {
        return false;
    }
    // Must look like an SPA route, never a script/bundle file. Intentionally strict (no dots /
    // encoded chars) to avoid false positives; matches observed routes such as /quick-launch,
    // /partial-registration and /mainTabs/tabs/home. The bare root "/" is rejected: no observed
    // event uses it, and root-attributed inline bootstrap code must stay reportable.
    if (!/^\/[A-Za-z0-9\-_][A-Za-z0-9\-_\/]*$/.test(pathname)) return false;
    if (/\.(m?js|cjs)$/i.test(pathname)) return false;
    if (/(main|polyfills|vendor|runtime|chunk|scripts)[.\-]/i.test(pathname)) return false;
    return true;
}

/**
 * True when the frames show the known injected-script signature: a contiguous run of frames
 * strictly alternating between exactly two minified 1–3 char names (Jk, Lk, Jk, ...), optionally
 * preceded OR followed by a small block of unnamed frames. Unnamed frames interleaved with the
 * recursion, too many unnamed frames, or any readable function name disqualify the event.
 */
export function hasMinifiedMutualRecursion(frames: FrameLike[]): boolean {
    const fns = (frames ?? []).map(frame => frame?.function);

    // Locate the single contiguous named block.
    const firstNamed = fns.findIndex(fn => !isUnnamed(fn));
    if (firstNamed === -1) return false;
    let lastNamed = fns.length - 1;
    while (isUnnamed(fns[lastNamed])) lastNamed--;

    const leadingUnnamed = firstNamed;
    const trailingUnnamed = fns.length - 1 - lastNamed;
    // Unnamed frames may sit at one end only, and must be few.
    if (leadingUnnamed > 0 && trailingUnnamed > 0) return false;
    if (leadingUnnamed + trailingUnnamed > MAX_UNNAMED_FRAMES) return false;

    const named = fns.slice(firstNamed, lastNamed + 1);
    // No unnamed frames inside the recursion block.
    if (named.some(isUnnamed)) return false;
    if (named.length < MIN_RECURSIVE_FRAMES) return false;
    if (!named.every(fn => MINIFIED_FN_RE.test(fn as string))) return false;
    if (new Set(named).size !== 2) return false;
    for (let i = 1; i < named.length; i++) {
        if (named[i] === named[i - 1]) return false;
        if (i >= 2 && named[i] !== named[i - 2]) return false;
    }
    return true;
}

export function isChromiumIOSInjectedScriptStackOverflow(
    event: EventLike,
    documentOrigin: string,
    appHost: string,
    userAgent?: string | null,
): boolean {
    if (!event || !documentOrigin) return false;

    // Deliberately strict: EVERY exception value must carry the signature. With chained/wrapper
    // exceptions we prefer a false negative (event reported) over hiding a real app error.
    const exceptionValues = event.exception?.values ?? [];
    if (exceptionValues.length === 0) return false;
    if (!exceptionValues.every(ex =>
        ex?.type === 'RangeError' && (ex.value ?? '').includes('Maximum call stack size exceeded')
    )) return false;
    if (!exceptionValues.every(ex => ex.mechanism?.type === 'onerror')) return false;

    if (!isChromiumIOSEnvironment(event, userAgent)) return false;

    // Computed once per event rather than per frame.
    const allowedOrigins = buildAllowedOrigins(documentOrigin, appHost);

    return exceptionValues.every(ex => {
        const frames = ex.stacktrace?.frames ?? [];
        if (frames.length === 0) return false;
        if (!frames.every(frame =>
            !!frame?.filename && isInjectedPageRouteFrame(frame.filename, allowedOrigins, documentOrigin)
        )) return false;
        return hasMinifiedMutualRecursion(frames);
    });
}
